import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';

export default function FiberCableRepairPage() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    { icon: 'ri-flashlight-line', title: 'Fusion Splicing', desc: 'Elektrik arkı ile fiber uçların kaynaştırılması, minimum kayıp garantisi' },
    { icon: 'ri-tools-line', title: 'Mekanik Ek', desc: 'Hızlı müdahale gereken durumlarda mekanik konnektör ile geçici veya kalıcı ek' },
    { icon: 'ri-line-chart-line', title: 'OTDR Test', desc: 'Tamir sonrası fiber hattın kayıp değerlerinin ölçümü ve raporlanması' },
    { icon: 'ri-time-line', title: 'Acil Müdahale', desc: '7/24 kesintisiz hizmet, ortalama 2 saat içinde olay yerine ulaşım' },
  ];

  const repairTypes = [
    {
      title: 'Kazı Nedeniyle Kesilen Fiber',
      desc: 'İnşaat, yol çalışması veya kazı sırasında kesilen fiber kabloların onarımı',
      icon: 'ri-hammer-line',
    },
    {
      title: 'Hasar Görmüş Fiber Kablo',
      desc: 'Ezilme, çekme veya darbe sonucu hasar gören fiber kabloların tamiri',
      icon: 'ri-alert-line',
    },
    {
      title: 'Bina İçi Fiber Arıza',
      desc: 'Riser kablo, kat dağıtımı veya ODF bölgesindeki fiber arızaların giderilmesi',
      icon: 'ri-building-line',
    },
    {
      title: 'Fabrika Fiber Tamiri',
      desc: 'Endüstriyel tesislerde makine ağları ve otomasyon sistemlerindeki fiber onarımı',
      icon: 'ri-factory-line',
    },
  ];

  const process = [
    { step: '1', title: 'Arıza Bildirimi', desc: 'Telefon veya WhatsApp ile 7/24 arıza bildirimi alınır' },
    { step: '2', title: 'Ekip Yönlendirme', desc: 'En yakın sertifikalı ekip olay yerine yönlendirilir' },
    { step: '3', title: 'Arıza Tespiti', desc: 'OTDR cihazı ile arıza noktası hassas şekilde tespit edilir' },
    { step: '4', title: 'Fiber Ek İşlemi', desc: 'Fusion splicing veya mekanik ek yöntemi ile tamir yapılır' },
    { step: '5', title: 'Test ve Ölçüm', desc: 'OTDR ve güç ölçer ile fiber hattın kalitesi test edilir' },
    { step: '6', title: 'Raporlama', desc: 'Tamir raporu ve ölçüm sonuçları müşteriye teslim edilir' },
  ];

  const faqData = [
    {
      question: 'Fiber optik kablo tamiri ne kadar sürer?',
      answer: 'Standart bir fiber kablo tamiri ortalama 2-4 saat sürmektedir. Arızanın konumu, fiber sayısı ve erişim koşullarına göre süre değişebilir. Acil durumlarda ekibimiz 2 saat içinde olay yerine ulaşır.',
    },
    {
      question: 'Fusion splicing ile mekanik ek arasındaki fark nedir?',
      answer: 'Fusion splicing elektrik arkı ile fiber uçların kaynaştırılması yöntemidir ve 0.02-0.05 dB gibi çok düşük kayıp değerleri sağlar. Mekanik ek ise fiber uçların mekanik konnektör içinde hizalanması yöntemidir, daha hızlıdır ancak 0.1-0.5 dB kayıp oluşturur.',
    },
    {
      question: 'Hangi bölgelerde fiber kablo tamiri hizmeti veriyorsunuz?',
      answer: 'İstanbul Avrupa ve Anadolu yakası, Kocaeli, Gebze, Tuzla, Dilovası, Çayırova, Bursa, Sakarya, Yalova, Tekirdağ ve Çorlu bölgelerinde aktif olarak fiber kablo tamiri hizmeti sunuyoruz.',
    },
    {
      question: 'Fiber kablo tamiri sonrası garanti veriyor musunuz?',
      answer: 'Evet, tüm fiber ek işlemlerimiz için 2 yıl garanti veriyoruz. Tamir sonrası OTDR test raporu ile fiber hattın kalitesini belgelendiriyoruz.',
    },
    {
      question: 'Acil fiber arıza durumunda ne yapmalıyım?',
      answer: '7/24 acil servis hattımızı arayarak veya WhatsApp üzerinden bize ulaşabilirsiniz. Ekibimiz en kısa sürede olay yerine ulaşarak arızayı giderir.',
    },
  ];

  const locations = [
    'İstanbul Avrupa', 'İstanbul Anadolu', 'Kocaeli', 'Gebze', 'Tuzla', 'Dilovası',
    'Çayırova', 'Bursa', 'Sakarya', 'Yalova', 'Tekirdağ', 'Çorlu'
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <title>Fiber Optik Kablo Tamiri İstanbul | Fusion Splicing ve Acil Fiber Onarım</title>
      <meta name="description" content="İstanbul, Kocaeli, Gebze ve çevre illerde profesyonel fiber optik kablo tamiri hizmeti. Fusion splicing, OTDR test, 7/24 acil fiber arıza müdahalei. Sertifikalı ekip, 2 saat yanıt süresi." />
      <meta name="keywords" content="fiber kablo tamiri, fiber optik tamir, fusion splicing, fiber ek, OTDR test, İstanbul fiber tamiri, Kocaeli fiber onarım, acil fiber servis, kesilen fiber kablo" />
      <link rel="canonical" href="https://fiberek.com/fiber-hizmetleri/fiber-kablo-tamiri" />
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <div className="min-h-screen bg-dark-950">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-dark-950 to-cyan-900/20"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-blue-500 to-transparent animate-pulse-slow"></div>
            <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-cyan-500 to-transparent animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-6">
                  <i className="ri-tools-line text-blue-400"></i>
                  <span className="text-blue-400 text-sm font-medium">Profesyonel Fiber Tamir</span>
                </div>

                <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                  Fiber Optik Kablo
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                    Tamiri Hizmeti
                  </span>
                </h1>

                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  İstanbul, Kocaeli, Gebze ve çevre illerde kesilen veya hasar gören fiber optik kabloların profesyonel onarımı. 
                  Fusion splicing yöntemi ile minimum kayıp garantisi, OTDR test ve 7/24 acil müdahale hizmeti.
                </p>

                <div className="flex flex-wrap gap-4 mb-8">
                  <a
                    href="tel:+905537165561"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-300 font-semibold shadow-lg shadow-blue-500/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line"></i>
                    Acil Arıza
                  </a>
                  <a
                    href="https://wa.me/905537165561"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line"></i>
                    WhatsApp
                  </a>
                </div>

                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <div className="text-3xl font-bold text-blue-400 mb-1">2 Saat</div>
                    <div className="text-sm text-gray-400">Ortalama Yanıt</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-cyan-400 mb-1">10+ Yıl</div>
                    <div className="text-sm text-gray-400">Deneyim</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-indigo-400 mb-1">7/24</div>
                    <div className="text-sm text-gray-400">Acil Servis</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative w-full h-96 rounded-2xl overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/a16c606348789efd5f81df2f994bed61.jpg"
                    alt="Fiber Optik Kablo Tamiri"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-transparent to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  ref={ref}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-6 border border-white/10 hover:border-blue-500/50 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-2xl text-blue-400`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 bg-gradient-to-br from-blue-900/10 via-dark-950 to-cyan-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Fiber Optik Kablo Tamiri Nedir?
                </h2>
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Fiber optik kablo tamiri, kesilen, hasar gören veya performans kaybı yaşayan fiber optik kabloların profesyonel yöntemlerle onarılması işlemidir. 
                    Bu işlem, fiber hattın kesintisiz ve yüksek kalitede veri iletimini sağlamak için kritik öneme sahiptir.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Fiber kablo tamirinde en yaygın kullanılan yöntem <strong className="text-white">fusion splicing</strong> (kaynaklı ek) yöntemidir. 
                    Bu yöntemde, fiber uçlar hassas bir şekilde temizlenir, kesilir ve elektrik arkı ile kaynaştırılır. 
                    Fusion splicing yöntemi ile 0.02-0.05 dB gibi çok düşük kayıp değerleri elde edilir.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Acil durumlarda veya geçici çözümlerde <strong className="text-white">mekanik ek</strong> yöntemi kullanılabilir. 
                    Mekanik ek, fiber uçların özel bir konnektör içinde hizalanması ile gerçekleştirilir ve daha hızlı uygulanabilir.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Tamir işlemi sonrasında mutlaka <strong className="text-white">OTDR (Optical Time Domain Reflectometer)</strong> cihazı ile test yapılmalı ve 
                    fiber hattın kayıp değerleri ölçülmelidir. Bu ölçüm, tamirin kalitesini garanti altına alır.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Hangi Durumlarda Fiber Tamir Gerekir?
                </h2>
                <div className="space-y-4">
                  {repairTypes.map((type, index) => (
                    <motion.div
                      key={type.title}
                      initial={{ opacity: 0, x: 20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: index * 0.1 }}
                      className="bg-dark-900 rounded-xl p-6 border border-white/10"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className={`${type.icon} text-xl text-blue-400`}></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white mb-2">{type.title}</h3>
                          <p className="text-gray-400 text-sm">{type.desc}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Fiber Kablo Tamir Süreci
              </h2>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                Arıza bildiriminden teslimat raporuna kadar profesyonel süreç yönetimi
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {process.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="relative bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl p-6 border border-white/10"
                >
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-blue-500/30">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 mt-4">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Details */}
        <section className="py-20 bg-gradient-to-br from-blue-900/10 via-dark-950 to-cyan-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Fusion Splicing Teknik Detayları
                </h2>
                <div className="space-y-6">
                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Kayıp Değerleri</h3>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-blue-400 mt-1"></i>
                        <span>Singlemode fiber: 0.02-0.05 dB tipik kayıp</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-blue-400 mt-1"></i>
                        <span>Multimode fiber: 0.05-0.10 dB tipik kayıp</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-blue-400 mt-1"></i>
                        <span>Mekanik ek: 0.1-0.5 dB kayıp aralığı</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Desteklenen Fiber Tipleri</h3>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-blue-400 mt-1"></i>
                        <span>Singlemode: OS1, OS2 (9/125 µm)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-blue-400 mt-1"></i>
                        <span>Multimode: OM1, OM2, OM3, OM4, OM5</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-blue-400 mt-1"></i>
                        <span>Özel fiber tipler: Bend-insensitive, armored</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <h3 className="text-xl font-bold text-white mb-3">Kullanılan Ekipmanlar</h3>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-blue-400 mt-1"></i>
                        <span>Fusion Splicer: Fujikura, Sumitomo marka cihazlar</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-blue-400 mt-1"></i>
                        <span>OTDR: Fluke, EXFO, Anritsu test cihazları</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-blue-400 mt-1"></i>
                        <span>Fiber Cleaver: Yüksek hassasiyetli kesme aletleri</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Neden Bizi Tercih Etmelisiniz?
                </h2>
                <div className="space-y-6">
                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-2xl text-blue-400"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">Sertifikalı Ekip</h3>
                        <p className="text-gray-400">
                          10+ yıl deneyimli, uluslararası sertifikalara sahip fiber optik uzmanları. 
                          Fujikura ve Sumitomo fusion splicer eğitimleri tamamlanmış teknik kadro.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-2xl text-cyan-400"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">Hızlı Müdahale</h3>
                        <p className="text-gray-400">
                          7/24 acil servis hattı ile kesintisiz hizmet. İstanbul ve çevre illerde ortalama 2 saat içinde 
                          olay yerine ulaşım. Mobil ekipman ile her lokasyonda tamir imkanı.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-list-line text-2xl text-indigo-400"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">Detaylı Raporlama</h3>
                        <p className="text-gray-400">
                          Her tamir sonrası OTDR test raporu, kayıp değerleri ve fiber hat kalite analizi. 
                          Dijital arşivleme ile geçmiş tüm işlemlere erişim imkanı.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-900 rounded-xl p-6 border border-white/10">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-star-line text-2xl text-violet-400"></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">2 Yıl Garanti</h3>
                        <p className="text-gray-400">
                          Tüm fiber ek işlemlerimiz için 2 yıl garanti veriyoruz. Garanti kapsamında herhangi bir 
                          sorun yaşanması durumunda ücretsiz yeniden müdahale.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Locations */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Fiber Kablo Tamiri Hizmet Bölgelerimiz
              </h2>
              <p className="text-gray-400">
                İstanbul ve çevre illerde fiber optik kablo tamiri hizmeti veriyoruz
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {locations.map((location) => (
                <span
                  key={location}
                  className="px-6 py-3 bg-dark-800 border border-white/10 rounded-lg text-gray-300 hover:border-blue-500 hover:text-blue-400 transition-all duration-300 whitespace-nowrap"
                >
                  {location}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-br from-blue-900/10 via-dark-950 to-cyan-900/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Sıkça Sorulan Sorular
              </h2>
              <p className="text-gray-400">
                Fiber kablo tamiri hakkında merak edilenler
              </p>
            </div>

            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <motion.details
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-dark-900 rounded-xl border border-white/10 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-2xl text-blue-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </motion.details>
              ))}
            </div>
          </div>
        </section>

        {/* Blog & Rehberler Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Blog & Rehberler</h2>
              <p className="text-gray-400">Fiber optik teknolojileri hakkında detaylı bilgiler</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=fiber%20optic%20cable%20repair%20fusion%20splicing%20process%2C%20technician%20working%20with%20precision%20equipment%2C%20blue%20glowing%20fiber%20cables%2C%20professional%20technical%20environment%20with%20modern%20splicing%20tools%2C%20detailed%20close-up%20of%20fiber%20connection%20work&width=400&height=250&seq=fiber-repair-blog1&orientation=landscape"
                    alt="Fiber Optik Kablo Tamiri"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-blue-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fiber Optik Kablo Tamiri Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Kesilen veya hasar gören fiber optik kabloların fusion splicing yöntemi ile profesyonel onarım teknikleri ve aşamaları.</p>
                  <a
                    href="/blog/fiber-optik-kablo-tamiri-nasil-yapilir"
                    className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=OTDR%20fiber%20optic%20testing%20equipment%20display%20showing%20measurement%20results%2C%20professional%20technician%20analyzing%20fiber%20test%20data%2C%20modern%20testing%20laboratory%20with%20technical%20instruments%2C%20detailed%20screen%20showing%20fiber%20trace%20graph&width=400&height=250&seq=otdr-test-blog&orientation=landscape"
                    alt="OTDR Testi"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-blue-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">OTDR Testi Nedir ve Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Fiber optik hatların kalite kontrolü ve arıza tespiti için kullanılan OTDR test yöntemleri ve uygulama aşamaları.</p>
                  <a
                    href="/blog/otdr-testi-nedir"
                    className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-dark-900 to-dark-800 rounded-xl overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=fusion%20splicing%20fiber%20optic%20process%2C%20close-up%20view%20of%20fiber%20fusion%20splicer%20machine%20in%20operation%2C%20precise%20fiber%20alignment%20and%20welding%2C%20professional%20technical%20environment%20with%20glowing%20fiber%20cables&width=400&height=250&seq=fusion-splicing-blog&orientation=landscape"
                    alt="Fusion Splicing"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-blue-500/90 text-white text-xs font-semibold rounded-full">Fiber Optik</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">Fusion Splicing Nedir? Nasıl Yapılır?</h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">Fiber optik kabloların kaynaştırılarak birleştirilmesi işlemi olan fusion splicing tekniği ve uygulama detayları.</p>
                  <a
                    href="/blog/fusion-splicing-nedir"
                    className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm font-semibold"
                  >
                    Okumaya Devam Et
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Acil Fiber Arıza Müdahalesi
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              7/24 kesintisiz hizmet, 2 saat içinde olay yerine ulaşım garantisi
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="tel:+905537165561"
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-blue-500/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                +90 553 716 55 61
              </a>
              <a
                href="https://wa.me/905537165561"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all duration-300 font-semibold text-lg shadow-lg shadow-emerald-500/30 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp ile Yaz
              </a>
            </div>
          </div>
        </section>

        <Footer />
        <FloatingButtons />
      </div>
    </>
  );
}